import {
  differenceInDays,
  differenceInYears,
  eachMonthOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth
} from 'date-fns';
import { StartOfWeekFormatSetting } from '../../../models/graphql';

export type ConfigChartTicks = {
  showLabelAfterEveryNTicks: number;
  displayMonthly?: boolean;
  displayWeekly?: boolean;
};
type ReferenceAreaCoordinates = { x1: number; x2: number; y1: number; y2: number };

export type FillingDataConfig = {
  referenceAreaPostFillingCoordinates?: ReferenceAreaCoordinates;
  referenceAreaPreFillingCoordinates?: ReferenceAreaCoordinates;
  hasPreFillingData?: boolean;
  hasPostFillingData?: boolean;
  hasFillingData: boolean;
};

export type NormalizedChartPoint = {
  rawValue: number | undefined;
  normalizedValue?: number | undefined;
  value?: number;
  fakeValue?: number;
  fakeValueDashedLine?: number;
};
export const createTicksConfigs = (startDate: Date, endDate: Date): ConfigChartTicks => {
  const qtyDays = getQtyDaysBetweenRange(startDate, endDate);
  if (qtyDays >= 367) {
    return {
      displayMonthly: true,
      showLabelAfterEveryNTicks: 1
    };
  }

  if (qtyDays >= 101) {
    return {
      displayWeekly: true,
      showLabelAfterEveryNTicks: qtyDays >= 186 ? 3 : 1
    };
  }
  if (qtyDays >= 66) {
    return {
      showLabelAfterEveryNTicks: 6
    };
  }
  return { showLabelAfterEveryNTicks: qtyDays >= 32 ? 2 : 1 };
};

export type TickChart = {
  date: Date;
  showMonthLabel: boolean;
  hideTick?: boolean;
};

export const createTicksChart = (dates: Date[]): TickChart[] => {
  return dates.map(date => ({
    date,
    showMonthLabel: false
  }));
};

export const verifyWhichTicksShouldRender = (
  chartTicks: TickChart[],
  ticksConfig: ConfigChartTicks,
  hasPreFill: boolean,
  hasPostFill: boolean,
  isLineChart: boolean,
  startDate: Date,
  dayStartOfWeek: StartOfWeekFormatSetting
): TickChart[] => {
  const ticks = [...chartTicks];
  if (ticks.length === 0) {
    return [];
  }
  const renderTickEveryNTicks = ticksConfig.showLabelAfterEveryNTicks;
  const isWeekly = ticksConfig.displayWeekly;
  const isMonthly = ticksConfig.displayMonthly;

  // We dont show filling date on bar charts
  if (!isLineChart && hasPreFill) {
    ticks.shift();
    ticks[0].hideTick = false;
    ticks[0].showMonthLabel = true;
  }
  if (!isLineChart && hasPostFill) {
    ticks.pop();
  }

  if (isMonthly) {
    ticks.forEach((tick, index) => {
      tick.hideTick = index % renderTickEveryNTicks !== 0;
      tick.showMonthLabel = true;
    });
    ticks[0].hideTick = true;
    if (hasPreFill && isLineChart) {
      ticks[1].hideTick = isSameDay(startDate, endOfMonth(startDate)) ? false : true;
    } else if (!isLineChart) {
      ticks[0].hideTick = false;
    }
    if (hasPostFill && isLineChart) {
      const secondLastTick = ticks[ticks.length - 2];
      if (isMonthly && secondLastTick) {
        secondLastTick.hideTick = true;
      }
    }
  } else if (isWeekly) {
    let secondTick: TickChart | undefined;
    let secondLastTick: TickChart | undefined;
    const originalLength = ticks.length;
    // removes the tick from the fill data to the ticks list to not interfere with the label calculation
    if (hasPreFill && isLineChart && ticks.length > 1) {
      secondTick = ticks.splice(1, 1)[0];
    }
    if (hasPostFill && isLineChart && ticks.length > 1) {
      const secondLastIndex = hasPreFill ? originalLength - 3 : originalLength - 2;
      secondLastTick = ticks.splice(secondLastIndex, 1)[0];
    }

    const months = eachMonthOfInterval({ start: ticks[0]?.date, end: ticks[ticks.length - 1]?.date });
    ticks.forEach((tick, index) => {
      const shouldShowMonth = isSameMonth(months[0], tick.date);
      //In the bar chart we need to align the tick with the grid line
      if (!isLineChart && index !== 0 && renderTickEveryNTicks === 3 && index !== 3) {
        tick.hideTick = (index - 3) % (renderTickEveryNTicks + 1) !== 0;
      } else {
        tick.hideTick = index % renderTickEveryNTicks !== 0;
      }

      tick.showMonthLabel = shouldShowMonth || renderTickEveryNTicks === 3;
      if (shouldShowMonth) {
        months.shift();
      }
    });

    // put back the tick from the fill data to the ticks list
    if (hasPostFill && isLineChart && secondLastTick !== undefined) {
      secondLastTick.hideTick = true;
      ticks.splice(ticks.length - 1, 0, secondLastTick);
    }

    if (hasPreFill && isLineChart && secondTick !== undefined) {
      secondTick.hideTick = isSameDay(
        startDate,
        endOfWeek(startDate, { weekStartsOn: dayStartOfWeek === StartOfWeekFormatSetting.SUNDAY ? 0 : 1 })
      )
        ? false
        : true;
      ticks.splice(1, 0, secondTick);
    }
  } else {
    const months = eachMonthOfInterval({ start: ticks[0]?.date, end: ticks[ticks.length - 1]?.date });
    ticks.forEach((tick, index) => {
      let shouldHideTick = index % renderTickEveryNTicks !== 0;
      if (!isLineChart && renderTickEveryNTicks === 6 && index !== 6 && index !== 0) {
        shouldHideTick = (index - 6) % (renderTickEveryNTicks + 1) !== 0;
      }
      tick.hideTick = shouldHideTick;
      const shouldShowMonth = isSameMonth(months[0], tick.date) && !shouldHideTick;
      tick.showMonthLabel = shouldShowMonth;
      if (shouldShowMonth) {
        months.shift();
      }
    });
  }
  return ticks;
};
export const getQtyDaysBetweenRange = (startDate: Date, endDate: Date) => {
  const daysDifference = differenceInDays(endDate, startDate);

  // Add 1 to include both start and end dates
  return daysDifference + 1;
};

export const getIsGroupedByWeek = (startDate: Date, endDate: Date) => {
  const qtyDays = getQtyDaysBetweenRange(startDate, endDate);
  return qtyDays >= 101 && Math.abs(differenceInYears(startDate, endDate)) < 1;
};

export const getNormalizedChartPoint = (obj: number | Date | NormalizedChartPoint): NormalizedChartPoint | null => {
  if (typeof obj !== 'object' || obj === null || obj instanceof Date) {
    return null;
  }

  const chartPoint = obj;

  if (typeof chartPoint.rawValue !== 'number' && typeof chartPoint.rawValue !== 'undefined') {
    return null;
  }

  if (
    'normalizedValue' in chartPoint &&
    typeof chartPoint.normalizedValue !== 'number' &&
    typeof chartPoint.normalizedValue !== 'undefined'
  ) {
    return null;
  }

  if ('value' in chartPoint && typeof chartPoint.value !== 'number' && typeof chartPoint.value !== 'undefined') {
    return null;
  }

  return chartPoint;
};

export const shouldHideGridLinesEveryFourLines = (startDate: Date, endDate: Date) =>
  getIsGroupedByWeek(startDate, endDate) && getQtyDaysBetweenRange(startDate, endDate) >= 186;
export const shouldHideGridLinesEverySevenLines = (startDate: Date, endDate: Date) => {
  const qtyDays = getQtyDaysBetweenRange(startDate, endDate);
  return !getIsGroupedByWeek(startDate, endDate) && qtyDays >= 66 && qtyDays <= 100;
};
